import React, { useState } from "react";
import axios from "axios";
import API_BASE_URL from "../api";
import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  Phone,
  Send,
  MessageSquare,
  Sparkles,
  CheckCircle2,
  Building2,
  Clock
} from "lucide-react";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const { firstName, lastName, email, phone, message } = formData;

  const handleOnChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg("");

    try {
      const response = await axios.post(`${API_BASE_URL}/api/query`, {
        fullName: `${firstName} ${lastName}`.trim(),
        email,
        phone,
        message,
      });

      if (response.status === 200 || response.status === 201) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Error submitting query:", error);
      setErrorMsg(
        error.response?.data?.message || "Failed to submit query. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 font-sans text-slate-900 pb-20">
      {/* Top Hero Dark Emerald Header for Transparent Navbar Contrast */}
      <div className="bg-gradient-to-br from-[#0E3B2E] via-[#092b21] to-[#041611] text-white pt-36 pb-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Subtle Grid Accent Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#10B981_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 backdrop-blur-md px-4 py-1.5 rounded-full mb-4 shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-xs sm:text-sm font-bold text-emerald-300 tracking-wide uppercase">
              Get In Touch With Us
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-6xl font-extrabold text-white tracking-tight leading-tight"
          >
            Contact Us
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-sm sm:text-lg text-emerald-100/90 mt-3 font-medium leading-relaxed max-w-2xl mx-auto"
          >
            Have a question or want to streamline your fleet emission compliance? Reach out to our sustainability team below.
          </motion.p>
        </div>
      </div>

      {/* Success Notification Toast */}
      {showSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-24 right-6 z-50 bg-[#0E3B2E] text-white border border-emerald-400 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3"
        >
          <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
          <div>
            <h4 className="font-bold text-sm">Message Sent Successfully!</h4>
            <p className="text-xs text-emerald-200">Our team will get back to you shortly.</p>
          </div>
        </motion.div>
      )}

      {/* 50/50 Split Screen Layout Container */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-10 relative z-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-white border border-slate-200/90 rounded-3xl shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12"
        >
          {/* LEFT HALF (50%): Contact Info Details & Map */}
          <div className="lg:col-span-6 bg-gradient-to-br from-[#0E3B2E] via-[#092b21] to-[#041611] text-white p-8 sm:p-10 flex flex-col justify-between relative overflow-hidden">
            {/* Subtle Grid Accent Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(#10B981_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

            <div className="relative z-10 space-y-8">
              <div>
                <span className="text-xs font-extrabold uppercase tracking-widest text-emerald-400 bg-emerald-950/60 border border-emerald-500/30 px-3 py-1 rounded-full">
                  Contact Details
                </span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-3">
                  Let's Connect
                </h2>
                <p className="text-xs sm:text-sm text-emerald-200/80 mt-1 leading-relaxed">
                  Reach out to our eco-logistics team directly via email, phone, or visit our headquarters.
                </p>
              </div>

              {/* Contact List */}
              <div className="space-y-6">
                {/* Email Support */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 text-emerald-400">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Email Support</h4>
                    <a href="mailto:team@tsil.net.in" className="block text-xs sm:text-sm text-emerald-200 hover:text-white transition font-medium mt-0.5">
                      team@tsil.net.in
                    </a>
                  </div>
                </div>

                {/* Office Location */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 text-emerald-400">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Corporate Office</h4>
                    <a
                      href="https://maps.app.goo.gl/rRJx9bAN9PjNQMfe7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs sm:text-sm text-emerald-200 hover:text-white transition font-medium mt-0.5 leading-relaxed"
                    >
                      609, Tower II, Pearls Omaxe, Netaji Subhash Place, Pitampura, New Delhi - 110034, India.
                    </a>
                  </div>
                </div>

                {/* Phone Number */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center shrink-0 text-emerald-400">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Call Us Directly</h4>
                    <a href="tel:+919661829944" className="block text-xs sm:text-sm text-emerald-200 hover:text-white transition font-semibold mt-0.5">
                      +91-96618 29944
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Embedded Google Map Preview */}
            <div className="relative z-10 mt-8 rounded-2xl overflow-hidden border border-white/20 shadow-md h-48 sm:h-56">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28911.023034541106!2d77.22371!3d28.6333333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd7fc38a8187%3A0x7abb73aa8c418882!2sParivahan%20Vings%20(KAL)%20Private%20Limited!5e0!3m2!1sen!2sin!4v1690137192828!5m2!1sen!2sin"
                className="w-full h-full border-0"
                allowFullScreen
                loading="lazy"
                title="Office Location Map"
              />
            </div>
          </div>

          {/* RIGHT HALF (50%): Clean Crisp White Contact Form */}
          <div className="lg:col-span-6 bg-white p-8 sm:p-10 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Send Us A Message</h3>
                  <p className="text-xs text-slate-500">Fill in the form below and we'll respond promptly</p>
                </div>
              </div>

              {errorMsg && (
                <div className="mb-4 p-3.5 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
                  {errorMsg}
                </div>
              )}

              <form onSubmit={handleOnSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={firstName}
                      onChange={handleOnChange}
                      placeholder="Enter first name"
                      required
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm rounded-xl px-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={lastName}
                      onChange={handleOnChange}
                      placeholder="Enter last name"
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm rounded-xl px-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={handleOnChange}
                      placeholder="example@gmail.com"
                      required
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm rounded-xl px-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={phone}
                      onChange={handleOnChange}
                      placeholder="+91-9876543210"
                      className="w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm rounded-xl px-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition font-medium"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-extrabold text-slate-700 uppercase tracking-wider mb-1.5">
                    Your Query / Message *
                  </label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={handleOnChange}
                    placeholder="Enter your message or sustainability query here..."
                    rows="4"
                    required
                    className="w-full bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 text-sm rounded-xl px-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition font-medium"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold text-base py-3.5 rounded-xl shadow-md transition transform hover:scale-[1.01] flex items-center justify-center gap-2 disabled:opacity-50 mt-2"
                >
                  {submitting ? (
                    <span>Submitting Message...</span>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <Send className="w-4 h-4 text-emerald-400" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactUs;